// src/theme/glassTokens.ts
//
// The dark-glass surface vocabulary, in ONE place.
//
// Pages wrapped in <VideoGlassContainer variant="dark"> (the WelcomeHeroCard /
// BulkUploadResults chrome: looping video, deepNavy→royalBlue gradient, frosted card)
// need ink and surfaces tuned for a DARK background. The app-wide
// `theme.custom.contentText.*` tokens are the opposite — they resolve to deepNavy
// (#092047) and blueGray, correct for a white card and effectively invisible here.
//
// Every value below is lifted verbatim from BulkUploadResults, which is the reference
// implementation of this surface. Import these instead of hand-tinting components, so
// a page ported later inherits the same vocabulary rather than approximating it.
//
// ⚠️ Only use these in a component that is ALWAYS rendered on the dark glass. A
// component shared with a light surface must keep the theme tokens.

import { alpha } from "@mui/material";
import { athenaColors } from "./palette";

export const glass = {
  // ── Ink ────────────────────────────────────────────────────────────────────
  textPrimary: athenaColors.white,
  textSecondary: alpha(athenaColors.white, 0.65),
  textMuted: alpha(athenaColors.white, 0.45),
  /** For a value that is deliberately absent (an em-dash in a matrix, "—"). */
  textAbsent: alpha(athenaColors.white, 0.35),

  // ── Surfaces ───────────────────────────────────────────────────────────────
  /** Inset card / sub-panel sitting on the glass. */
  panel: alpha(athenaColors.deepNavy, 0.35),
  panelBorder: alpha(athenaColors.white, 0.1),
  /** A slightly deeper inset, e.g. a summary strip. */
  panelSunken: alpha(athenaColors.deepNavy, 0.4),

  // ── Tables ─────────────────────────────────────────────────────────────────
  tableFrame: alpha(athenaColors.deepNavy, 0.2),
  tableFrameBorder: alpha(athenaColors.white, 0.12),
  /** Sticky header cells — opaque enough to hide rows scrolling beneath. */
  tableHeader: alpha(athenaColors.deepNavy, 0.85),
  headerDivider: alpha(athenaColors.white, 0.15),
  rowDivider: alpha(athenaColors.white, 0.06),
  rowHover: alpha(athenaColors.periwinkle, 0.08),

  // ── Accents ────────────────────────────────────────────────────────────────
  // MUI's info/success/warning mains are tuned for LIGHT surfaces (success.main is a
  // dark green — mud on navy). These four are the brand's light family and every one
  // clears contrast against deepNavy while staying distinguishable from each other.
  accent: athenaColors.periwinkle,
  accentAlt: athenaColors.lightPurple,
  accentWarm: athenaColors.lightOrange,
  accentStrong: athenaColors.orange,

  // ── Interactive ────────────────────────────────────────────────────────────
  /** Primary CTA. NEVER use theme.palette.primary.dark here: it resolves to
   *  #092047 — deepNavy, i.e. the exact colour of the card behind the button. */
  ctaBg: athenaColors.royalBlue,
  ctaBorder: alpha(athenaColors.periwinkle, 0.4),
  /** Selected state for tabs / toggles. primary.main is royalBlue: too dark here. */
  selectedBg: alpha(athenaColors.periwinkle, 0.18),
  selectedBorder: alpha(athenaColors.periwinkle, 0.45),
  /** Outlined chip / secondary control, as used by BulkUploadResults' header. */
  chipBg: alpha(athenaColors.deepNavy, 0.4),
  chipBorder: alpha(athenaColors.periwinkle, 0.4),
} as const;

export default glass;

// ── Tables ─────────────────────────────────────────────────────────────────
/**
 * Default ink and dividers for a table on the dark glass. Spread onto the
 * `<Table sx={...}>`.
 *
 * Needed because MUI's TableCell sets `color: theme.palette.text.primary`
 * itself, so a cell does NOT inherit ink from its container — and in the
 * athena_gradient theme that token is deepNavy, the colour of the card behind
 * it. Header cells resolve to text.secondary, which is royalBlue: brand
 * colour, but ~1.4:1 here and effectively buried.
 *
 * ⚠️ A cell needing a DIFFERENT tone must put the colour on a CHILD element
 * (wrap the content in a Typography). Any parent rule here is a descendant
 * selector and outranks that cell's own `sx`, so setting it on the cell has
 * no effect.
 */
export const glassTableSx = {
  "& thead th": {
    color: glass.textSecondary,
    borderColor: glass.headerDivider,
  },
  "& tbody td": {
    color: glass.textPrimary,
    borderColor: glass.rowDivider,
  },
} as const;

// ── Inputs ─────────────────────────────────────────────────────────────────
/**
 * A TextField on the dark glass. Spread onto the `<TextField sx={...}>`.
 *
 * ⚠️ The ink MUST go on `.MuiOutlinedInput-input`, not on the root.
 * athena_gradient sets `MuiOutlinedInput.styleOverrides.input.color` to
 * deepNavy, i.e. directly on the <input> element, and a colour applied
 * directly to an element always beats one inherited from its parent, whatever
 * the source order. Setting `color` on `.MuiOutlinedInput-root` therefore
 * leaves the typed text deepNavy on a navy field: the placeholder looks right
 * and the moment anyone types, the text vanishes.
 *
 * The theme's own dark-dialog block solves it exactly this way; this is that
 * recipe, lifted so every dark surface can share it.
 *
 * The three background rules are also required: the theme gives the root a
 * white 0.45 fill with its own hover and focus states.
 */
/**
 * One control strip. The patient search, the ask bar and the Add-patient CTA
 * are three controls stacked on the same column, so a 10px radius next to a
 * pill next to a 12px button reads as three unrelated widgets. Single source,
 * so they cannot drift apart again.
 */
export const glassControl = { height: 52, radius: 3 } as const;

export const glassInputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: alpha(athenaColors.white, 0.07),
    backdropFilter: "blur(8px)",
    "&:hover": { backgroundColor: alpha(athenaColors.white, 0.1) },
    "&.Mui-focused": {
      backgroundColor: alpha(athenaColors.white, 0.12),
      boxShadow: `0 0 0 3px ${alpha(athenaColors.periwinkle, 0.18)}`,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: alpha(athenaColors.white, 0.18),
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: alpha(athenaColors.periwinkle, 0.45),
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: athenaColors.periwinkle,
    },
  },
  "& .MuiOutlinedInput-input": {
    color: athenaColors.white,
    "&::placeholder": {
      color: alpha(athenaColors.white, 0.55),
      opacity: 1,
    },
  },
} as const;

// ── Content ────────────────────────────────────────────────────────────────
/**
 * Ink for arbitrary MUI content rendered on the dark glass. Spread onto the
 * CONTAINER; every rule is a descendant selector, so it reaches children that
 * were written for a white card without touching them.
 *
 * Lifted verbatim from VideoGlassContainer, which has carried this block since
 * BulkUploadResults and is still its only other consumer. It lives here now
 * because the NL result card needs exactly the same treatment and was
 * duplicating none of it.
 *
 * ⚠️ These are descendant selectors at (0,2,0); a child's own `sx` is (0,1,0)
 * and LOSES to them. That is the point for text that was never converted, but
 * it means a deliberate accent inside (a periwinkle icon, a coloured chip) has
 * to be re-asserted at higher specificity, not set on the element.
 */
export const glassContentSx = {
  color: athenaColors.white,
  "& .MuiTypography-root": { color: athenaColors.white },
  "& .MuiTypography-colorTextSecondary, & .MuiTypography-caption": {
    color: `${alpha(athenaColors.white, 0.6)} !important`,
  },
  "& .MuiFormControlLabel-label": { color: athenaColors.white },
  "& .MuiOutlinedInput-root": {
    backgroundColor: alpha(athenaColors.white, 0.07),
    color: athenaColors.white,
    "& fieldset": { borderColor: alpha(athenaColors.periwinkle, 0.3) },
    "&:hover fieldset": { borderColor: alpha(athenaColors.periwinkle, 0.55) },
    "&.Mui-focused fieldset": { borderColor: athenaColors.periwinkle },
  },
  "& .MuiOutlinedInput-input": {
    color: athenaColors.white,
    "&::placeholder": { color: alpha(athenaColors.white, 0.45), opacity: 1 },
  },
  "& .MuiInputLabel-root": {
    color: alpha(athenaColors.white, 0.6),
    "&.Mui-focused": { color: athenaColors.periwinkle },
  },
  "& .MuiFormHelperText-root": { color: alpha(athenaColors.white, 0.5) },
  "& .MuiSelect-icon": { color: alpha(athenaColors.white, 0.5) },
  "& .MuiSwitch-root .MuiSwitch-track": { backgroundColor: alpha(athenaColors.white, 0.2) },
  "& .MuiSwitch-root .Mui-checked + .MuiSwitch-track": {
    backgroundColor: `${alpha(athenaColors.periwinkle, 0.5)} !important`,
  },
  "& .MuiSwitch-root .Mui-checked .MuiSwitch-thumb": { color: athenaColors.periwinkle },
  "& .MuiCheckbox-root": {
    color: alpha(athenaColors.white, 0.5),
    "&.Mui-checked": { color: athenaColors.periwinkle },
  },
  "& .MuiChip-root": { color: athenaColors.white, borderColor: alpha(athenaColors.periwinkle, 0.3) },
  "& .MuiTabs-root": {
    "& .MuiTab-root": { color: alpha(athenaColors.white, 0.6), "&.Mui-selected": { color: athenaColors.white } },
    "& .MuiTabs-indicator": { backgroundColor: athenaColors.periwinkle },
  },
  "& .MuiButton-contained": {
    backgroundColor: athenaColors.royalBlue,
    color: athenaColors.white,
    "&:hover": { backgroundColor: alpha(athenaColors.royalBlue, 0.85) },
  },
  "& .MuiButton-outlined": {
    color: alpha(athenaColors.white, 0.85),
    borderColor: alpha(athenaColors.periwinkle, 0.4),
    "&:hover": { borderColor: athenaColors.periwinkle, backgroundColor: alpha(athenaColors.periwinkle, 0.1) },
  },
  "& .MuiIconButton-root": { color: alpha(athenaColors.white, 0.7) },
  "& .MuiSvgIcon-root": { color: alpha(athenaColors.white, 0.5) },
} as const;

// ── Lists ──────────────────────────────────────────────────────────────────
/**
 * Ink for a list of rows on the dark glass (the patient search results).
 *
 * Those rows were written for a white card: most Typographies inherit
 * deepNavy, and a few set `theme.palette.text.secondary`, which is royalBlue.
 * Both disappear here. Re-inking each of the fifteen call sites would leave
 * the next row added to the list broken again, so the ink goes on the
 * CONTAINER as a descendant rule, which also outranks the per-element `sx`
 * those few rows set.
 */
export const glassListSx = {
  color: glass.textPrimary,
  "& .MuiTypography-root": { color: glass.textPrimary },
  "& .MuiTypography-caption, & .MuiTypography-body2": {
    color: glass.textSecondary,
  },
  "& .MuiIconButton-root": { color: glass.textSecondary },
  "& .MuiDivider-root": { borderColor: glass.rowDivider },
} as const;

// ── Charts ─────────────────────────────────────────────────────────────────
// `useChartTheme` is shared with the LIGHT dashboard charts: its axis ink
// resolves through theme.custom.contentText to blueGray, and its tooltip to
// background.paper. Both vanish on this surface. Re-ink per chart here rather
// than changing the shared hook, which would break the dashboard.

/** Re-ink `chartTheme.baseAxis` for the dark glass. */
export const glassAxis = <T extends { axisLabel?: Record<string, unknown> }>(
  baseAxis: T
) => ({
  ...baseAxis,
  axisLine: { lineStyle: { color: glass.headerDivider } },
  axisLabel: { ...(baseAxis.axisLabel || {}), color: glass.textSecondary },
  splitLine: { lineStyle: { color: glass.rowDivider, type: "dashed" as const } },
});

/** Re-ink `chartTheme.baseTooltip` for the dark glass. */
export const glassChartTooltip = <
  T extends { textStyle?: Record<string, unknown> }
>(
  baseTooltip: T
) => ({
  ...baseTooltip,
  backgroundColor: glass.tableHeader,
  borderColor: glass.panelBorder,
  textStyle: { ...(baseTooltip.textStyle || {}), color: glass.textPrimary },
});
